<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
  v-card.neutral200
    v-card-text.pt-8.pb-0
      .mb-4
        .d-flex.align-center.mb-4
          v-btn.mr-4(icon small)
            v-icon(color="primary" @click="back") mdi-close
          .text-2xl.font-semibold.neutral700--text {{ invoice.invoice_no }}
      v-row
        v-col(cols=8)
          v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
            v-row.pb-2.pb-md-0(no-gutters)
              v-col(cols="12" md="8")
                v-tabs(color="basic700" :height="44")
                  v-tab.text-sm.font-semibold.text-none.px-4 {{ $t('all') }}
              v-col(cols="12" md="4" align="end")
                .d-flex.align-center
                  v-spacer
                  v-btn(@click="clickSearch" icon color="neutral500")
                    v-icon(size="20") $yzi-search-sm
                  v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
                  v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="showUploadDialog = true") {{ $t('upload') }}
            v-divider
            
            template(v-if="totalCount === 0 && !isFetching")
              v-card.round-xl.text-center.pa-8.border-dashed.mt-4.mb-16(outlined)
                v-avatar.mb-8(color="neutral300" size="54" rounded="circle")
                  v-icon(color="neutral500" size="36") $yzi-file-06
                .mb-1.font-semibold.text-md This invoice don’t have any uploaded files yet.
                .text-sm.neutral500--text Start adding files to this patient by clicking “Upload”

            template(v-else)
              .data-table
                v-data-table.mt-sm-4.pb-6(:headers="headers" :items="attachments" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" @click:row="selectFile" loader-height="2" :header-props="{ sortIcon: '$yzi-triangle' }" item-class="red")
                  template(v-slot:item.name="{ item }")
                    .h9 {{ item.document.filename }}
                  template(v-slot:item.created="{ item }")
                    div {{ item.created | convertServerDate2 }}
                  template(v-slot:item.uploaded_by="{ item }")
                    template(v-if="item.uploaded_by")
                      v-tooltip(top color="neutral700")
                        template(#activator="{ on, attrs }")
                          div(v-bind="attrs" v-on="on")
                            y-chip.cursor-help(v-bind="attrs" v-on="on" :label="item.uploaded_by.name" size="sm" chip-class="pa-1" outlined)
                              template(#prepend)
                                v-avatar.mr-1(size="20" color="neutral300")
                                  v-icon(color="neutral500" size="15") $yzi-user-03
                        div {{ item.uploaded_by.name }}
                  template(v-slot:item.action="{ item }")
                    .py-3.text-right
                      v-btn(icon small @click="downloadFile(item)")
                        v-icon(size=20) $yzi-download-01
                      v-btn(icon small @click="deleteItem(item)")
                        v-icon(size=20) $yzi-trash-03

              .d-flex.align-center.mt-6(v-if="totalCount > pageSize")
                v-spacer
                yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" text="Item list" @input="pageChanged")

        v-col(cols=4)
          v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
            .my-16.py-16.text-center(v-if="!selectedFile")
              .text-md.font-medium.mb-8 {{ $t('no_document_selected') }}
              .neutral500--text {{ $t('no_document_selected_description') }}
            div(v-else)
              .text-md.font-medium.text-center.mb-2 {{ selectedFile.name }}
              .font-medium.mb-3 {{  selectedFile.document.filename }}
              .text-right.mt-2
                y-button.mr-2(label="Delete" @click="deleteItem(selectedFile)" text-color="danger" size="sm" underline prependIcon="$yzi-trash-03" iconSize="20")
                y-button(label="View file" @click="viewFile" size="sm" outlined prependIcon="$yzi-eye" iconSize="20")
              v-divider.my-4
              .font-medium.mb-3.text-md {{ $t('file_details') }}
              .mb-4
                .text-sm.font-medium {{ $t('file_type') }}
                .text-md {{ selectedFile.document.type }}
              .mb-4
                .text-sm.font-medium {{ $t('created') }}
                .text-md {{ selectedFile.created | convertServerDate2 }}
              div
                .text-sm.font-medium.mb-1 {{ $t('uploaded_by') }}
                y-chip.cursor-help(:label="selectedFile.uploaded_by.name" size="sm" chip-class="pa-1" outlined)
                  template(#prepend)
                    v-avatar.mr-1(size="20" color="neutral300")
                      v-icon(color="neutral500" size="15") $yzi-user-03

    v-card-actions.white.pa-0.ma-0
      .d-flex.flex-column.grow
        v-divider
        .d-flex.justify-end.align-center.my-4.mx-8
          v-btn.button-sm.underline.mr-2(text @click="back") {{ $t('cancel') }}
          v-btn.button-sm(depressed color="primary" @click="back") {{ $t('done') }}

    .dialogs
      upload-invoice-attachment-dialog(v-if="showUploadDialog" :open.sync="showUploadDialog" :id="id" @onUploaded="reload")
      delete-invoice-attachment-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :id="id" :file="selectedFile" @onDeleted="reload")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DeleteInvoiceAttachmentDialog from '@/components/clinic/inventory/invoice/DeleteInvoiceAttachmentDialog.vue'
import UploadInvoiceAttachmentDialog from '@/components/clinic/inventory/invoice/UploadInvoiceAttachmentDialog.vue'

export default {
  name: 'InvoiceAttachmentDialog',
  components: { UploadInvoiceAttachmentDialog, DeleteInvoiceAttachmentDialog },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    id: {
      required: true,
    },
    invoice: {
      required: true,
      type: Object,
      default: null
    }
  },
  data () {
    return {
      searchText: null,
      showSearchField: false,
      pageSize: 20,
      currentPage: 1,
      totalCount: 0,
      isFetching: false,
      headers: [
        { text: 'NAME', value: 'name' },
        { text: 'UPLOAD DATE', value: 'created' },
        { text: 'UPLOADED BY', value: 'uploaded_by' },
        { text: '', value: 'action', sortable: false }
      ],
      attachments: [],
      selectedFile: null,
      showDeleteDialog: false,
      showUploadDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
  },
  mounted () {
    this.fetchAttachments()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAttachments () {
      this.isFetching = true
      const params = {
        page: this.currentPage,
        page_size: this.pageSize
      }
      this.$axios.get(`/clinics/${this.company.slug}/inventory_invoices/${this.id}/documents`, { params })
        .then(resp => {
          this.selectedFile = null
          const data = resp.data
          this.attachments = data.results
          this.totalCount = data.count
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    back () {
      if (!this.isFetching) {
        this.$emit('onUpdateCount', this.totalCount)
      }
      this.showDialog = false
    },
    reload () {
      this.fetchAttachments()
    },
    deleteItem (item) {
      this.showDeleteDialog = true
      this.selectedFile = item
    },
    selectFile (item) {
      this.selectedFile = item
    },
    clickSearch () {
    },
    pageChanged () {
      this.fetchAttachments()
    },
    editFile (item) {
      this.selectedFile = item
      this.showEditDialog = true
    },
    downloadFile (item) {
      window.open(item.document.file, '_blank')
    },
    viewFile () {
      window.open(this.selectedFile.document.file, '_blank')
    }
  },
}
</script>

<style lang="scss" scoped>
.border-outline {
  border: 1px solid #EEEEEE !important;
}
::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
</style>
